import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import {
  AI_BLOCK_KINDS,
  BLOCK_KINDS,
  BLOCK_KIND_LABELS,
  PAGE_TYPES,
  SITE_TYPES,
  type BlockKind,
} from '@seo/contracts';
import { CreatePanel } from '../components/CreatePanel';
import { DataTable, type Column } from '../components/DataTable';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import { zodResolver } from '../lib/zod-resolver';
import classes from './EntityPage.module.css';

/**
 * Конструктор шаблонів (§6.1) — екран, якого не існувало.
 *
 * `/templates` віддавав `PlaceholderPage` з обіцянкою «Фаза 2», а неймспейс `templates` мав
 * девʼять процедур із нулем викликачів: список, картку, готовність, створення, оновлення,
 * додавання сторінок і блоків, зміну порядку й видалення. Тобто конструктор був написаний
 * цілком і не мав жодних дверей.
 *
 * Головне, що екран мусить показувати чесно, — **готовність**. `assessTemplate` розрізняє
 * `problems` (зупиняють збірку) і `warnings` (видно й не зупиняють), і без цього поділу
 * оператор дізнавався б про проблему шаблону вже на збірці сайту, за три екрани звідси.
 */

interface TemplateRow {
  id: string;
  name: string;
  siteType: string;
  pagesCount: number;
  blocksCount: number;
  sitesUsing: number;
  hideEmptyBlocks: boolean;
  singleOrderForAll: boolean;
  shuffleTextBlocks: boolean;
}

const createSchema = z.object({
  name: z.string().trim().min(1, 'Вкажіть назву шаблону').max(200),
  siteType: z.enum(SITE_TYPES),
});
type CreateValues = z.infer<typeof createSchema>;

const SITE_TYPE_LABELS: Record<string, string> = {
  monobrand: 'монобренд',
  monoslot: 'моно-слот',
  review_cluster: 'ревʼю-кластер',
  drop: 'дроп',
};

export function TemplatesPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [newPageType, setNewPageType] = useState<string>('home');
  const [newPageLabel, setNewPageLabel] = useState('');
  const [newPageUrl, setNewPageUrl] = useState('/');
  const [blockPageId, setBlockPageId] = useState<string | null>(null);
  const [newBlockKind, setNewBlockKind] = useState<string>('ai_text');
  const [newBlockProfileId, setNewBlockProfileId] = useState<string | null>(null);
  /** Чернетка правки сторінки: `null` — оператор не чіпав, показуємо збережене. */
  const [pageDraft, setPageDraft] = useState<{ id: string; label: string; urlPattern: string } | null>(
    null,
  );
  const [confirmDelete, setConfirmDelete] = useState(false);
  const canWrite = useCan('ai_generation', 'write');
  const canDelete = useCan('ai_generation', 'delete');

  const list = useQuery(
    trpc.templates.list.queryOptions({
      ...(search ? { search } : {}),
      pagination: { limit: 100, direction: 'asc' },
    }),
  );

  const detail = useQuery({
    ...trpc.templates.byId.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  /**
   * Готовність — окремий запит, бо це не поле шаблону, а вердикт про нього: набір проблем і
   * попереджень, який перераховується щоразу, коли склад змінюється.
   */
  const readiness = useQuery({
    ...trpc.templates.readiness.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.templates.pathKey() });
  };

  const createForm = useForm<CreateValues>({
    initialValues: { name: '', siteType: 'monobrand' },
    validate: zodResolver(createSchema),
  });

  const create = useMutation(
    trpc.templates.create.mutationOptions({
      onSuccess: async (result) => {
        createForm.reset();
        setCreateOpen(false);
        setSelectedId(result.id);
        await invalidate();
      },
    }),
  );
  const update = useMutation(trpc.templates.update.mutationOptions({ onSuccess: invalidate }));
  const addPage = useMutation(
    trpc.templates.pages.add.mutationOptions({
      onSuccess: async () => {
        setNewPageLabel('');
        await invalidate();
      },
    }),
  );
  const updatePage = useMutation(
    trpc.templates.pages.update.mutationOptions({
      onSuccess: async () => {
        setPageDraft(null);
        await invalidate();
      },
    }),
  );
  const removePage = useMutation(
    trpc.templates.pages.remove.mutationOptions({ onSuccess: invalidate }),
  );
  const reorderPages = useMutation(
    trpc.templates.pages.reorder.mutationOptions({ onSuccess: invalidate }),
  );
  const addBlock = useMutation(trpc.templates.blocks.add.mutationOptions({ onSuccess: invalidate }));
  const updateBlock = useMutation(
    trpc.templates.blocks.update.mutationOptions({ onSuccess: invalidate }),
  );
  const removeBlock = useMutation(
    trpc.templates.blocks.remove.mutationOptions({ onSuccess: invalidate }),
  );
  const reorderBlocks = useMutation(
    trpc.templates.blocks.reorder.mutationOptions({ onSuccess: invalidate }),
  );
  /**
   * Видалення шаблону — два кроки, як у решті реєстрів, і воно **відмовляє**, поки шаблоном
   * користується сайт: `sites.template_id` оголошений як `on delete set null`, тож мовчазне
   * видалення відвʼязало б шаблон від усієї сітки без жодної помилки.
   */
  const removeTemplate = useMutation(
    trpc.templates.delete.mutationOptions({
      onSuccess: async () => {
        setSelectedId(null);
        setConfirmDelete(false);
        await invalidate();
      },
    }),
  );

  /** Профілі для привʼязки до AI-блока: без профілю блок не проходить перевірку складу. */
  const promptList = useQuery({
    ...trpc.prompts.list.queryOptions({
      latestOnly: true,
      pagination: { limit: 100, direction: 'asc' },
    }),
    enabled: selectedId !== null,
  });

  const columns: Column<TemplateRow>[] = [
    { key: 'name', header: 'Шаблон', render: (row) => <span>{row.name}</span> },
    {
      key: 'siteType',
      header: 'Тип сайту',
      render: (row) => (
        <Badge size="sm" variant="default" data-testid={`template-type-${row.name}`}>
          {SITE_TYPE_LABELS[row.siteType] ?? row.siteType}
        </Badge>
      ),
    },
    {
      key: 'pages',
      header: 'Сторінок',
      align: 'right',
      render: (row) => (
        <span className={classes.mono} data-testid={`template-pages-${row.name}`}>
          {row.pagesCount}
        </span>
      ),
    },
    {
      key: 'blocks',
      header: 'Блоків',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.blocksCount}</span>,
    },
    {
      key: 'sites',
      header: 'Сайтів',
      align: 'right',
      /** Радіус вибуху: скільки сайтів перезбереться, якщо чіпнути цей шаблон. */
      render: (row) => (
        <span className={classes.mono} data-testid={`template-sites-${row.name}`}>
          {row.sitesUsing}
        </span>
      ),
    },
  ];

  const template = detail.data;
  const mutationError =
    addPage.error ??
    updatePage.error ??
    removePage.error ??
    reorderPages.error ??
    addBlock.error ??
    updateBlock.error ??
    removeBlock.error ??
    reorderBlocks.error ??
    update.error;

  return (
    <Stack gap="md">
      <Group gap="sm" align="flex-end" wrap="wrap">
        <TextInput
          label="Пошук"
          placeholder="назва шаблону"
          value={search}
          onChange={(event) => {
            setSearch(event.currentTarget.value);
          }}
          data-testid="template-search"
          style={{ width: 220 }}
        />
        <CreatePanel
          onReset={() => {
            create.reset();
            createForm.reset();
          }}
          zone="ai_generation"
          label="Додати шаблон"
          testId="template"
          open={createOpen}
          onOpenChange={setCreateOpen}
          busy={create.isPending}
          error={create.error}
          onSubmit={createForm.onSubmit((values) => {
            create.mutate(values);
          })}
        >
          <TextInput
            size="xs"
            placeholder="назва"
            data-testid="create-template-name"
            style={{ width: 200 }}
            {...createForm.getInputProps('name')}
          />
          <Select
            size="xs"
            allowDeselect={false}
            data={SITE_TYPES.map((value) => ({ value, label: SITE_TYPE_LABELS[value] ?? value }))}
            data-testid="create-template-site-type"
            style={{ width: 160 }}
            {...createForm.getInputProps('siteType')}
          />
        </CreatePanel>
      </Group>

      <DataTable
        rows={list.data?.items ?? []}
        columns={columns}
        loading={list.isPending}
        error={list.error}
        emptyMessage="Шаблонів немає. Створіть перший — без шаблону сайт не збереться."
        onRowClick={(row) => {
          setSelectedId(row.id);
          setBlockPageId(null);
        }}
        rowTestId={(row) => `template-row-${row.name}`}
        testId="template-table"
      />

      {selectedId === null ? null : detail.isPending ? (
        <Box className={classes.panel}>
          <Loader size="sm" />
        </Box>
      ) : template === undefined ? null : (
        <Box className={classes.panel} data-testid="template-detail">
          <Group justify="space-between" mb="sm">
            <Text className={classes.panelTitle}>{template.name}</Text>
            <Group gap={8}>
              {canDelete ? (
                confirmDelete ? (
                  <>
                    <Text className={classes.danger}>
                      Видалити «{template.name}» разом зі складом сторінок?
                    </Text>
                    <Button
                      size="compact-xs"
                      color="rose"
                      loading={removeTemplate.isPending}
                      data-testid="template-delete-confirm"
                      onClick={() => {
                        removeTemplate.mutate({ id: template.id });
                      }}
                    >
                      Видалити
                    </Button>
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      data-testid="template-delete-cancel"
                      onClick={() => {
                        setConfirmDelete(false);
                        removeTemplate.reset();
                      }}
                    >
                      Скасувати
                    </Button>
                  </>
                ) : (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="rose"
                    data-testid="template-delete"
                    onClick={() => {
                      setConfirmDelete(true);
                    }}
                  >
                    Видалити шаблон
                  </Button>
                )
              ) : null}
              <Button
                size="compact-xs"
                variant="subtle"
                data-testid="template-close"
                onClick={() => {
                  setSelectedId(null);
                  setConfirmDelete(false);
                }}
              >
                Закрити
              </Button>
            </Group>
          </Group>

          {removeTemplate.error == null ? null : (
            <Text className={classes.danger} mb={8} data-testid="template-delete-error" role="alert">
              {publicMessageOf(removeTemplate.error)}
            </Text>
          )}

          {/*
           * Вердикт про шаблон, а не його поле: `problems` зупиняють збірку, `warnings` — ні.
           * Без цього поділу оператор дізнавався б про проблему вже на збірці сайту, за три
           * екрани звідси.
           */}
          {readiness.data === undefined ? null : (
            <Stack gap={4} mb="sm" data-testid="template-readiness">
              <Badge
                size="sm"
                color={readiness.data.ready ? 'mint' : 'rose'}
                data-testid="template-ready"
              >
                {readiness.data.ready ? 'готовий до збірки' : 'збірка заблокована'}
              </Badge>
              {readiness.data.problems.map((problem) => (
                <Text key={problem} className={classes.danger} data-testid="template-problem">
                  {problem}
                </Text>
              ))}
              {readiness.data.warnings.map((warning) => (
                <Text key={warning} className={classes.warn} data-testid="template-warning">
                  {warning}
                </Text>
              ))}
            </Stack>
          )}

          <Group gap="lg" mb="sm" wrap="wrap">
            <Switch
              size="xs"
              label="Ховати порожні блоки"
              checked={template.hideEmptyBlocks}
              disabled={!canWrite}
              data-testid="template-hide-empty"
              onChange={(event) => {
                update.mutate({
                  id: template.id,
                  patch: { hideEmptyBlocks: event.currentTarget.checked },
                });
              }}
            />
            <Switch
              size="xs"
              label="Один порядок для всіх"
              checked={template.singleOrderForAll}
              disabled={!canWrite}
              data-testid="template-single-order"
              onChange={(event) => {
                update.mutate({
                  id: template.id,
                  patch: { singleOrderForAll: event.currentTarget.checked },
                });
              }}
            />
          </Group>

          <Stack gap="md">
            {template.pages.length === 0 ? (
              <Text className={classes.danger} data-testid="template-no-pages">
                Сторінок немає — шаблон не збере жодного сайту
              </Text>
            ) : (
              template.pages.map((page, pageIndex) => (
                <Box key={page.id} data-testid={`template-page-${page.pageType}`}>
                  <Group gap={8} mb={4}>
                    {/*
                     * Тип сторінки — бейдж, а не поле: він унікальний у межах шаблону й ним
                     * матеріалізуються сторінки сайту, тож зміна типу на місці лишила б уже
                     * зібрані сайти з блоками одного типу під адресою іншого.
                     */}
                    <Badge size="sm" variant="default">
                      {page.pageType}
                    </Badge>
                    {canWrite ? (
                      <>
                        <TextInput
                          size="xs"
                          value={pageDraft?.id === page.id ? pageDraft.label : page.label}
                          data-testid={`template-page-label-${page.pageType}`}
                          style={{ width: 170 }}
                          onChange={(event) => {
                            setPageDraft({
                              id: page.id,
                              label: event.currentTarget.value,
                              urlPattern: pageDraft?.id === page.id ? pageDraft.urlPattern : page.urlPattern,
                            });
                          }}
                        />
                        <TextInput
                          size="xs"
                          value={pageDraft?.id === page.id ? pageDraft.urlPattern : page.urlPattern}
                          data-testid={`template-page-pattern-${page.pageType}`}
                          style={{ width: 140 }}
                          onChange={(event) => {
                            setPageDraft({
                              id: page.id,
                              label: pageDraft?.id === page.id ? pageDraft.label : page.label,
                              urlPattern: event.currentTarget.value,
                            });
                          }}
                        />
                        <Button
                          size="compact-xs"
                          variant="default"
                          disabled={pageDraft?.id !== page.id}
                          loading={updatePage.isPending && pageDraft?.id === page.id}
                          data-testid={`template-page-save-${page.pageType}`}
                          onClick={() => {
                            if (pageDraft?.id !== page.id) return;
                            updatePage.mutate({
                              id: page.id,
                              patch: { label: pageDraft.label, urlPattern: pageDraft.urlPattern },
                            });
                          }}
                        >
                          Зберегти
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          disabled={pageIndex === 0}
                          data-testid={`template-page-up-${page.pageType}`}
                          onClick={() => {
                            const ids = template.pages.map((each) => each.id);
                            const previous = ids[pageIndex - 1];
                            const current = ids[pageIndex];
                            if (previous === undefined || current === undefined) return;
                            ids[pageIndex - 1] = current;
                            ids[pageIndex] = previous;
                            reorderPages.mutate({ templateId: template.id, orderedPageIds: ids });
                          }}
                        >
                          ↑
                        </Button>
                        <Button
                          size="compact-xs"
                          variant="subtle"
                          color="rose"
                          data-testid={`template-page-remove-${page.pageType}`}
                          onClick={() => {
                            removePage.mutate({ id: page.id });
                          }}
                        >
                          прибрати сторінку
                        </Button>
                      </>
                    ) : (
                      <>
                        <Text size="sm">{page.label}</Text>
                        <Text className={classes.mono}>{page.urlPattern}</Text>
                      </>
                    )}
                  </Group>

                  <Stack gap={2} pl="md">
                    {page.blocks.length === 0 ? (
                      <Text className={classes.dim} data-testid={`template-page-empty-${page.pageType}`}>
                        Блоків немає
                      </Text>
                    ) : (
                      page.blocks.map((block, index) => (
                        <Group key={block.id} gap={8} data-testid={`template-block-${block.id}`}>
                          <span className={classes.mono}>{block.sortOrder}</span>
                          <Text size="sm">{BLOCK_KIND_LABELS[block.blockKind] ?? block.blockKind}</Text>
                          {block.required ? (
                            <Badge size="xs" color="amber">
                              обовʼязковий
                            </Badge>
                          ) : null}
                          {block.promptProfileName === null ? null : (
                            <Text className={classes.dim} data-testid={`template-block-profile-${block.id}`}>
                              {block.promptProfileName} v{block.promptProfileVersion}
                            </Text>
                          )}
                          {/*
                           * Закріплення за версією навмисне: блок генерує тим самим текстом
                           * промпту, з якого зібрані сторінки. Але без цього рядка публікація
                           * нової версії виглядає як зламана — оператор публікує v3, бачить
                           * стару як «замінену» й не розуміє, чому сітка генерується
                           * по-старому. `resolveProfileFor` бере профіль блока не питаючи
                           * стану, і це єдине місце, де видно, що саме він візьме.
                           */}
                          {block.promptProfileLiveVersion === null ? null : (
                            <Badge size="xs" color="rose" data-testid={`template-block-pinned-${block.id}`}>
                              закріплено на v{block.promptProfileVersion} · опублікована v
                              {block.promptProfileLiveVersion}
                            </Badge>
                          )}
                          {canWrite ? (
                            <>
                              {/*
                               * Профіль привʼязується тут, а не лише на створенні блока: без
                               * нього `assessTemplate` тримає збірку, і полагодити це можна
                               * було тільки видаливши блок і додавши заново.
                               */}
                              <Select
                                size="xs"
                                searchable
                                clearable
                                placeholder="профіль"
                                data={(promptList.data?.items ?? []).map((item) => ({
                                  value: item.id,
                                  label: `${item.name} v${String(item.version)}`,
                                }))}
                                value={block.promptProfileId}
                                data-testid={`template-block-profile-select-${block.id}`}
                                style={{ width: 180 }}
                                onChange={(value) => {
                                  updateBlock.mutate({
                                    id: block.id,
                                    patch: { promptProfileId: value },
                                  });
                                }}
                              />
                              <Switch
                                size="xs"
                                label="обовʼязковий"
                                checked={block.required}
                                data-testid={`template-block-required-${block.id}`}
                                onChange={(event) => {
                                  updateBlock.mutate({
                                    id: block.id,
                                    patch: { required: event.currentTarget.checked },
                                  });
                                }}
                              />
                              {/*
                               * Порядок блоків — те, що читач бачить згори вниз. `reorder`
                               * приймає весь перелік id: часткове переставляння лишало б
                               * діри в `sort_order`.
                               */}
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                disabled={index === 0}
                                data-testid={`template-block-up-${block.id}`}
                                onClick={() => {
                                  const ids = page.blocks.map((b) => b.id);
                                  const swapped = [...ids];
                                  const previous = swapped[index - 1];
                                  const current = swapped[index];
                                  if (previous === undefined || current === undefined) return;
                                  swapped[index - 1] = current;
                                  swapped[index] = previous;
                                  reorderBlocks.mutate({
                                    templatePageId: page.id,
                                    orderedBlockIds: swapped,
                                  });
                                }}
                              >
                                ↑
                              </Button>
                              <Button
                                size="compact-xs"
                                variant="subtle"
                                data-testid={`template-block-remove-${block.id}`}
                                onClick={() => {
                                  removeBlock.mutate({ id: block.id });
                                }}
                              >
                                прибрати
                              </Button>
                            </>
                          ) : null}
                        </Group>
                      ))
                    )}

                    {canWrite ? (
                      <Group gap={8} mt={4}>
                        <Select
                          size="xs"
                          searchable
                          allowDeselect={false}
                          data={BLOCK_KINDS.map((value) => ({
                            value,
                            label: BLOCK_KIND_LABELS[value] ?? value,
                          }))}
                          value={blockPageId === page.id ? newBlockKind : 'ai_text'}
                          data-testid={`template-block-kind-${page.pageType}`}
                          style={{ width: 190 }}
                          onChange={(value) => {
                            setBlockPageId(page.id);
                            setNewBlockKind(value ?? 'ai_text');
                          }}
                        />
                        {/*
                          * Профіль питається **на створенні**, а не лише на вже доданому блоці.
                          *
                          * `addTemplateBlock` відмовляє AI-блоку без профілю, а форма про це не
                          * питала: додати такий блок було неможливо взагалі, і єдиною відповіддю
                          * лишався червоний рядок «згенерує невідомо що». Прив'язка нижче лагодила
                          * те, що не встигало створитись.
                          */}
                        {AI_BLOCK_KINDS.includes(
                          (blockPageId === page.id ? newBlockKind : 'ai_text') as BlockKind,
                        ) ? (
                          <Select
                            size="xs"
                            searchable
                            clearable
                            placeholder={
                              (promptList.data?.items ?? []).length === 0
                                ? 'профілів немає — заведіть у «Контент сайтів»'
                                : 'промпт-профіль'
                            }
                            data={(promptList.data?.items ?? []).map((item) => ({
                              value: item.id,
                              label: `${item.name} v${String(item.version)}`,
                            }))}
                            value={blockPageId === page.id ? newBlockProfileId : null}
                            data-testid={`template-block-new-profile-${page.pageType}`}
                            style={{ width: 210 }}
                            onChange={(value) => {
                              setBlockPageId(page.id);
                              setNewBlockProfileId(value);
                            }}
                          />
                        ) : null}
                        <Button
                          size="compact-xs"
                          variant="default"
                          loading={addBlock.isPending}
                          data-testid={`template-block-add-${page.pageType}`}
                          onClick={() => {
                            const blockKind = (
                              blockPageId === page.id ? newBlockKind : 'ai_text'
                            ) as (typeof BLOCK_KINDS)[number];
                            const profileId = blockPageId === page.id ? newBlockProfileId : null;
                            addBlock.mutate({
                              templatePageId: page.id,
                              block: {
                                blockKind,
                                ...(profileId === null ? {} : { promptProfileId: profileId }),
                              },
                            });
                          }}
                        >
                          + блок
                        </Button>
                      </Group>
                    ) : null}
                  </Stack>
                </Box>
              ))
            )}

            {canWrite ? (
              <Group gap="sm" align="flex-end" data-testid="template-add-page">
                <Select
                  size="xs"
                  label="Тип сторінки"
                  searchable
                  allowDeselect={false}
                  data={[...PAGE_TYPES]}
                  value={newPageType}
                  data-testid="template-page-type"
                  style={{ width: 150 }}
                  onChange={(value) => {
                    setNewPageType(value ?? 'home');
                  }}
                />
                <TextInput
                  size="xs"
                  label="Підпис"
                  value={newPageLabel}
                  data-testid="template-page-label"
                  style={{ width: 180 }}
                  onChange={(event) => {
                    setNewPageLabel(event.currentTarget.value);
                  }}
                />
                <TextInput
                  size="xs"
                  label="URL-патерн"
                  value={newPageUrl}
                  data-testid="template-page-url"
                  style={{ width: 150 }}
                  onChange={(event) => {
                    setNewPageUrl(event.currentTarget.value);
                  }}
                />
                <Button
                  size="compact-xs"
                  loading={addPage.isPending}
                  disabled={newPageLabel.trim() === ''}
                  data-testid="template-page-add"
                  onClick={() => {
                    addPage.mutate({
                      templateId: template.id,
                      page: {
                        pageType: newPageType as (typeof PAGE_TYPES)[number],
                        label: newPageLabel.trim(),
                        urlPattern: newPageUrl.trim(),
                      },
                    });
                  }}
                >
                  Додати сторінку
                </Button>
              </Group>
            ) : null}
          </Stack>

          {mutationError == null ? null : (
            <Text className={classes.danger} mt={8} data-testid="template-error" role="alert">
              {publicMessageOf(mutationError)}
            </Text>
          )}
        </Box>
      )}
    </Stack>
  );
}
